/**
 * 온습도로거 도입 시작 단계 — 현황 탭이 비어 있을 때(설치된 지점이 하나도 없을 때) 카드 자리에 보인다 (2026-11-05).
 *
 * 초기 도입 시점의 현황은 '설치 지점이 없습니다' 한 줄뿐이라 무엇부터 해야 하는지 알 수 없었다(빈 상태 실측).
 * 업무 흐름도의 작업 순서(① 지점 등록 → ② 장치 등록 → ③ 지점에 기기 설치 → ④ 첫 수신 확인)를 실제 데이터로 판정해
 * 끝난 단계는 체크, 지금 할 단계는 강조하고 버튼 한 번으로 그 화면 · 입력 창을 연다.
 * 기기를 처음 켜면 미등록 IMEI 로 거절 로그에 남는다 — 그 IMEI 를 바로 등록하는 길도 여기서 안내한다.
 */
import React from 'react';
import { Button, Tag } from 'antd';
import { CheckCircleFilled, EnvironmentOutlined, HddOutlined, SwapOutlined, CloudUploadOutlined, ApartmentOutlined, ExclamationCircleOutlined } from '@ant-design/icons';
import { gray } from '../theme/tokens';

type Props = {
  points: any[]; devices: any[]; rejects24h: number; canWrite: boolean;
  onNewPoint: () => void; onNewDevice: () => void; onGoTab: (k: string) => void; onGuide: (k: string) => void;
};

const ThLoggerSetup: React.FC<Props> = ({ points, devices, rejects24h, canWrite, onNewPoint, onNewDevice, onGoTab, onGuide }) => {
  const pts = points.filter(p => p.is_active);
  const devs = devices.filter(d => d.is_active);
  const installed = pts.filter(p => p.current).length;
  const received = devs.filter(d => d.last_uplink_at).length;
  const steps = [
    { icon: <EnvironmentOutlined />, title: '설치 지점 등록', done: pts.length > 0, stat: `${pts.length}곳`,
      desc: '하우스의 측정 위치. 코드는 현장 표지와 같게(예 H01-1). 기록의 주인이라 기기를 바꿔도 이어진다.',
      act: canWrite ? <Button size="small" type="primary" onClick={onNewPoint}>지점 등록</Button> : null },
    { icon: <HddOutlined />, title: '장치 등록 (IMEI)', done: devs.length > 0, stat: `${devs.length}대`,
      desc: '기기 라벨의 IMEI 15자리. 등록한 IMEI 만 저장된다 — 먼저 켰다면 거절 로그의 IMEI 를 그대로 등록할 수 있다.',
      act: canWrite ? <Button size="small" type="primary" onClick={onNewDevice}>장치 등록</Button> : null },
    { icon: <SwapOutlined />, title: '지점에 기기 설치', done: installed > 0, stat: `${installed}/${pts.length}곳`,
      desc: '설치 지점 → 해당 지점의 설치 버튼 → 기기 선택 · 설치 시각. 이 기록으로 측정값이 지점에 붙는다.',
      act: <Button size="small" onClick={() => onGoTab('points')}>설치 지점 열기</Button> },
    { icon: <CloudUploadOutlined />, title: '첫 수신 확인', done: received > 0, stat: `${received}대 수신`,
      desc: '유심 · AT 설정 후 버튼 1~3초 → 1~2분 안에 업링크 로그에 309B 행. 안 보이면 거절 로그 → 수신 확인 가이드.',
      act: <Button size="small" onClick={() => onGoTab('uplinks')}>업링크 로그</Button> },
  ];
  const cur = steps.findIndex(s => !s.done);
  return (
    <div style={{ maxWidth: 980, margin: '8px auto 0' }}>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 10, marginBottom: 10 }}>
        <b style={{ fontSize: 16, color: gray[900] }}>온습도로거 시작하기</b>
        <span style={{ fontSize: 12.5, color: gray[600] }}>설치된 지점이 생기면 이 자리에 지점별 현황 카드가 보입니다.</span>
        <Button size="small" type="link" icon={<ApartmentOutlined />} style={{ marginLeft: 'auto' }} onClick={() => onGuide('flow')}>업무 흐름도 보기</Button>
      </div>
      {rejects24h > 0 && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 12px', marginBottom: 10, borderRadius: 8, background: '#fff7ed', border: '1px solid #fed7aa', fontSize: 12.5, color: '#9a3412' }}>
          <ExclamationCircleOutlined />
          <span>최근 24시간 <b>거절 {rejects24h}건</b> — 기기를 먼저 켰다면 미등록 IMEI 입니다. 거절 로그에서 그 IMEI 를 바로 등록할 수 있습니다.</span>
          <Button size="small" style={{ marginLeft: 'auto' }} onClick={() => onGoTab('rejects')}>거절 로그</Button>
        </div>
      )}
      <ol style={{ listStyle: 'none', margin: 0, padding: 0, display: 'grid', gridTemplateColumns: 'repeat(4, minmax(0, 1fr))', gap: 10 }}>
        {steps.map((s, i) => {
          const now = i === cur;
          return (
            <li key={s.title} style={{
              background: '#fff', borderRadius: 10, padding: '12px 12px 10px', display: 'flex', flexDirection: 'column', gap: 6,
              border: `${now ? 2 : 1}px solid ${now ? '#4f46e5' : s.done ? '#bbf7d0' : gray[200]}`,
              boxShadow: now ? '0 0 0 4px rgba(79,70,229,.08)' : 'none', opacity: !s.done && !now ? 0.75 : 1,
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <span style={{ width: 24, height: 24, borderRadius: 12, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', fontWeight: 800, fontSize: 12,
                  background: s.done ? '#16a34a' : now ? '#4f46e5' : gray[200], color: s.done || now ? '#fff' : gray[600] }}>{s.done ? <CheckCircleFilled /> : i + 1}</span>
                <b style={{ fontSize: 13.5, color: gray[900] }}>{s.title}</b>
                <Tag style={{ margin: '0 0 0 auto' }} color={s.done ? 'green' : now ? 'geekblue' : undefined}>{s.done ? '완료' : now ? '지금' : '대기'}</Tag>
              </div>
              <div style={{ fontSize: 12, color: gray[600], lineHeight: 1.6, flex: 1 }}><span style={{ color: gray[400] }}>{s.icon}</span> {s.desc}</div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <span style={{ fontSize: 12, color: gray[500], fontVariantNumeric: 'tabular-nums' }}>{s.stat}</span>
                <span style={{ marginLeft: 'auto' }}>{s.act}</span>
              </div>
            </li>);
        })}
      </ol>
      <div style={{ fontSize: 12, color: gray[500], marginTop: 10 }}>
        현장 준비(유심 장착 · 블루투스 AT 설정 · 08:00 ATZ)는 설정 가이드 → 사용자 가이드 '기기 준비'에 있습니다.
        실기기 전에 <Button size="small" type="link" style={{ padding: 0, height: 'auto' }} onClick={() => onGuide('pcsim')}>PC 시뮬레이터</Button>로 같은 순서를 미리 해 볼 수 있습니다.
      </div>
    </div>
  );
};

export default ThLoggerSetup;
